import type { PostHog } from "@posthog/react";
import { usePostHog } from "@posthog/react";
import { parseError } from "evlog";
import { RotateCcw, Settings } from "lucide-react";
import { useState } from "react";

import { RestartConfirmDialog } from "#/components/game/RestartConfirmDialog";
import { Button } from "#/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "#/components/ui/dropdown-menu";
import { Switch } from "#/components/ui/switch";
import { useGameSelector } from "#/game/GameStoreProvider";
import { useRequestGameContext } from "#/lib/useRequestGameContext";
import { updateMyGameplaySetting } from "#/server/updateMyGameplaySetting";

function runToggle({
  apply,
  save,
  next,
  isAnonymous,
  posthog,
}: {
  apply: (value: boolean) => void;
  save: (enabled: boolean) => Promise<unknown>;
  next: boolean;
  isAnonymous: boolean;
  posthog: PostHog;
}) {
  apply(next);
  if (isAnonymous) return;
  void save(next).catch((err: unknown) => {
    apply(!next);
    const parsed = parseError(err);
    if (parsed.status >= 500) {
      posthog.captureException(err);
    }
  });
}

function SettingCheckboxItem({
  label,
  checked,
  apply,
  save,
}: {
  label: string;
  checked: boolean;
  apply: (value: boolean) => void;
  save: (enabled: boolean) => Promise<unknown>;
}) {
  const { isAnonymous } = useRequestGameContext();
  const posthog = usePostHog();
  return (
    <DropdownMenuCheckboxItem
      checked={checked}
      onCheckedChange={(next) => runToggle({ apply, save, next, isAnonymous, posthog })}
      className="justify-between pr-1.5 [&_[data-slot=dropdown-menu-checkbox-item-indicator]]:hidden"
    >
      {label}
      <Switch
        size="sm"
        checked={checked}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none"
      />
    </DropdownMenuCheckboxItem>
  );
}

function SettingsToggleGroup() {
  const fastPlayback = useGameSelector((s) => s.fastPlayback);
  const endTurnConfirmation = useGameSelector((s) => s.endTurnConfirmation);
  const disableAiPlayback = useGameSelector((s) => s.disableAiPlayback);
  const setFastPlayback = useGameSelector((s) => s.setFastPlayback);
  const setEndTurnConfirmation = useGameSelector((s) => s.setEndTurnConfirmation);
  const setDisableAiPlayback = useGameSelector((s) => s.setDisableAiPlayback);

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Game Settings</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <SettingCheckboxItem
        label="End Turn Confirmation"
        checked={endTurnConfirmation}
        apply={setEndTurnConfirmation}
        save={(enabled) =>
          updateMyGameplaySetting({ data: { setting: "endTurnConfirmation", enabled } })
        }
      />
      <SettingCheckboxItem
        label="Fast Animations"
        checked={fastPlayback}
        apply={setFastPlayback}
        save={(enabled) =>
          updateMyGameplaySetting({ data: { setting: "fastAnimations", enabled } })
        }
      />
      <SettingCheckboxItem
        label="Disable AI Playback"
        checked={disableAiPlayback}
        apply={setDisableAiPlayback}
        save={(enabled) =>
          updateMyGameplaySetting({ data: { setting: "disableAiPlayback", enabled } })
        }
      />
    </DropdownMenuGroup>
  );
}

export function GameSettingsMenu({
  onRestart,
  isRestarting,
}: {
  onRestart: () => void | Promise<void>;
  isRestarting: boolean;
}) {
  const [restartOpen, setRestartOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="outline" size="icon-lg" aria-label="Game settings">
              <Settings />
            </Button>
          }
        />
        <DropdownMenuContent side="top" align="end" sideOffset={8} className="w-56 min-w-56">
          <SettingsToggleGroup />
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            disabled={isRestarting}
            onClick={() => setRestartOpen(true)}
          >
            <RotateCcw />
            Restart
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <RestartConfirmDialog
        open={restartOpen}
        onOpenChange={setRestartOpen}
        onConfirm={onRestart}
        pending={isRestarting}
      />
    </>
  );
}
